/**
 * Static catalog + display helpers for the metadata repository UI.
 *
 * Pure constants and pure functions — no React, no async, no I/O.
 * Keeping these out of the page component means the table-rendering
 * components (`EntitySidebar`, `EntityTable`, etc.) can be unit-tested
 * in isolation and the ENTITIES list can be re-used in a future
 * `MetadataAdmin` page without dragging the whole orchestrator.
 */

// Mirror of backend's ENTITY_REGISTRY. Kept in sync by hand for now
// (small list, rarely changes).
export const ENTITIES = [
  { slug: "libraries",          label: "Libraries",                   source: "auto from DSPOBJD", importable: false },
  { slug: "objects",            label: "Objects",                     source: "DSPOBJD",           importable: true  },
  { slug: "physical-files",     label: "Physical files",              source: "DSPFD",             importable: true  },
  { slug: "fields",             label: "Fields",                      source: "DSPFFD",            importable: true  },
  { slug: "logical-files",      label: "Logical files",               source: "DSPFD (LF)",        importable: true  },
  { slug: "file-relationships", label: "File relationships",          source: "DSPDBR",            importable: true  },
  { slug: "programs",           label: "Programs",                    source: "DSPOBJD *PGM",      importable: true  },
  { slug: "program-references", label: "Program references",          source: "DSPPGMREF",         importable: true  },
  // Iter 36.d — journal & change history.
  { slug: "journal-entries",    label: "Journal entries",             source: "DSPJRN / CPYJRN",   importable: true  },
  { slug: "journal-receivers",  label: "Journal receivers",           source: "DSPJRNRCV",         importable: true  },
];

// Per-entity column order for the right-pane table.
export const COLUMN_PRESETS = {
  libraries:          ["library_name", "description", "created_at"],
  objects:            ["library_name", "object_name", "object_type", "attribute", "text_description", "source_file", "change_date"],
  "physical-files":   ["library_name", "file_name", "record_count", "record_length", "file_size", "journaled", "journal_name", "text_description"],
  fields:             ["library_name", "file_name", "field_name", "data_type", "field_length", "decimal_positions", "key_field", "column_heading", "field_text"],
  "logical-files":    ["library_name", "logical_name", "based_on_file", "access_path_shared", "select_omit"],
  "file-relationships": ["parent_library", "parent_file", "dependent_library", "dependent_object", "dependent_type"],
  programs:           ["library_name", "program_name", "object_type", "source_file", "source_member", "text_description"],
  "program-references": ["program_library", "program_name", "referenced_library", "referenced_object", "referenced_type"],
  "journal-entries":   ["journal_name", "sequence_number", "entry_type", "entry_at", "object_library", "object_name", "program_name", "user_profile", "job_name", "relative_record_number"],
  "journal-receivers": ["receiver_library", "receiver_name", "journal_name", "status", "first_sequence", "last_sequence", "size_bytes", "attached_at", "detached_at", "text_description"],
};

export const PAGE_SIZE = 100;

export function formatBytes(b) {
  if (!b || b < 1024) return `${b || 0} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatRelative(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.round(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
